import { useContext, useMemo } from "react";
import { Dayjs } from "dayjs";
import {
  BreadTransaction,
  CategoryType,
  FirestoreDocCategory,
} from "breadcommon";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";

// Sentinel node ids for the nodes that aren't backed by a category. Prefixed so
// they can't collide with a Firestore category id.
export const UNREVIEWED_NODE_ID = "__unreviewed__";
export const TOTAL_INCOME_NODE_ID = "__total_income__";
// One node forms the waist of the chart. Inflows converge on it and outflows
// leave it, and it carries a label on each side (see WaistLabels).
export const TOTAL_FLOW_NODE_ID = "__total_flow__";
export const TOTAL_EXPENSES_NODE_ID = "__total_expenses__";
export const TOTAL_SAVINGS_NODE_ID = "__total_savings__";
export const UNACCOUNTED_INFLOW_NODE_ID = "__unaccounted_inflow__";
export const UNACCOUNTED_OUTFLOW_NODE_ID = "__unaccounted_outflow__";

// Hex values matching the Tailwind tokens in tailwind.config.js (except where
// noted). Nivo needs real colors, not class names.
const INCOME_COLOR = "#3FD088"; // green-500
const TOTAL_INCOME_COLOR = "#2AAC6B"; // green-600
// Every band gradients to this where it meets the waist and back out of it on
// the far side, which is what makes the color continuous across the junction —
// bands regroup there, so a shared color is the only thing that can be.
//
// A desaturated purple rather than one off the purple ramp: those all sit too
// close to the savings purple to read as distinct, and a light neutral lands on
// top of the gray the unaccounted nodes use (which means "unknown" rather than
// "everything"). Muting is what buys a lighter bar that separates from both.
const TOTAL_FLOW_COLOR = "#574b73"; // muted lavender, no Tailwind token
const EXPENSE_COLOR = "#0ea5e9"; // blue-500 (sky-500)
const TOTAL_EXPENSES_COLOR = "#0284c7"; // blue-600 (sky-600)
const SAVINGS_COLOR = "#7d57ff"; // purple-500
const TOTAL_SAVINGS_COLOR = "#6930f7"; // purple-600
const BALANCING_COLOR = "#94a3b8"; // on-surface-400 (slate-400)
const UNREVIEWED_COLOR = "#FECF21"; // yellow-500, matching the unreviewed indicator

/**
 * Where a node should sit horizontally, for the cases where the column
 * d3-sankey would infer from the graph isn't the one we want. A number pins the
 * node to that column; "lastColumn" pins it to the rightmost one, whichever
 * that turns out to be.
 */
export type MoneyFlowColumnOverride = number | "lastColumn";

export interface MoneyFlowNode {
  id: string;
  nodeLabel: string;
  nodeColor: string;
  columnOverride?: MoneyFlowColumnOverride;
}

export interface MoneyFlowLink {
  /** The group represented by this band, also used for selection and drilldown. */
  drilldownNodeId: string;
  source: string;
  target: string;
  value: number;
}

export interface MoneyFlowData {
  nodes: MoneyFlowNode[];
  transactionsByNode: Map<string, BreadTransaction[]>;
  links: MoneyFlowLink[];
  /** Income categories that netted positive over the period. */
  totalIncome: number;
  /** Expense categories that netted as spending. */
  totalExpenses: number;
  /** Savings categories that netted as saving. */
  totalSavings: number;
  /** The value at the waist, which is both the total inflow and the total
   * outflow — they're equal by construction. */
  totalFlow: number;
  /**
   * How many nodes land in the most crowded column. The chart has to be tall
   * enough for that column's labels to stay readable.
   */
  maxColumnNodeCount: number;
  /**
   * The labels sitting in the chart's outer margins — the categories at the far
   * left and far right. Every other label falls in a gap between columns, so
   * these are the only ones the side margins have to be wide enough for.
   */
  outerLabels: { left: string[]; right: string[] };
  currency: string;
}

interface CategoryNet {
  /** Signed so that positive is money in, whatever the category's type. */
  net: number;
  type: CategoryType;
}

type CategoryNetEntry = [string, CategoryNet];

/**
 * Builds the Sankey data for the money flow report:
 *
 *   income categories ─→ Total Income ─┐          ┌─→ Total Expenses ─→ expense categories
 *                                      │          ├─→ Total Savings ──→ savings categories
 *   refund-dominant categories ────────┼→ waist ──┤
 *                                      │          ├─→ Unaccounted Outflow
 *   Unaccounted Inflow ────────────────┘          └─→ reversed income categories
 *
 * The waist is a single node, labelled "Total Inflow" on its left and "Total
 * Outflow" on its right. Those two figures are equal by construction — the
 * balancing nodes are what make them balance — so drawing them as two nodes
 * would spend a column implying a difference that can never exist.
 *
 * Everything converging on that one node is what produces the proportional
 * split: every source funds every destination in proportion to its share of the
 * period, without needing a link for each source/destination pair.
 *
 * Reviewed Silent categories are excluded entirely. That's how transfers between the
 * user's own accounts are categorized in Bread, and counting them would
 * double-count the same dollars as both an inflow and an outflow.
 * When enabled, all unreviewed transactions net into a separate node using
 * their original cash-flow signs, independent of category.
 */
export function useMoneyFlowData(
  beginDate: Dayjs,
  endDate: Dayjs,
  includeUnreviewed = false,
): MoneyFlowData {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const preferences = useContext(PreferencesContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const currency = preferences?.web?.default_currency ?? "USD";

  return useMemo(
    () =>
      buildMoneyFlowData(
        convertTransactionsToSpecificCurrency(
          Array.from(transactions.values()),
          currency,
        ),
        categories,
        beginDate,
        endDate,
        currency,
        includeUnreviewed,
      ),
    [
      transactions,
      categories,
      beginDate,
      endDate,
      currency,
      includeUnreviewed,
      convertTransactionsToSpecificCurrency,
    ],
  );
}

/**
 * The pure transformation behind useMoneyFlowData. Transactions are expected to
 * have already been converted into `currency`.
 */
export function buildMoneyFlowData(
  transactions: BreadTransaction[],
  categories: Map<string, FirestoreDocCategory>,
  beginDate: Dayjs,
  endDate: Dayjs,
  currency: string,
  includeUnreviewed = false,
): MoneyFlowData {
  const filteredTransactions = transactions.filter(
    (transaction) =>
      (transaction.reviewed || includeUnreviewed) &&
      transaction.date >= beginDate &&
      transaction.date <= endDate,
  );

  // Sign conventions differ by category type (see getSignedBudgetLineItemAmount
  // in budgetUtils): income amounts are positive, expense amounts are negative,
  // and savings amounts are positive for money moved *into* savings. Negating
  // savings puts every type on one scale where positive means money in, so a
  // category's direction is just the sign of its total.
  const netByCategory = new Map<string, CategoryNet>();

  let unreviewedNet = 0;
  filteredTransactions.forEach((transaction) => {
    // Use the transaction cash-flow sign regardless of any tentative category.
    // Include uncategorized and Silent transactions in this one net bucket too.
    if (!transaction.reviewed) {
      unreviewedNet += transaction.amount;
      return;
    }

    if (!transaction.categoryId) return;

    const category = categories.get(transaction.categoryId);
    if (!category) return;
    // CategoryType.Silent is intentionally skipped (see doc comment).
    if (category.type === CategoryType.Silent) return;

    const signedAmount =
      category.type === CategoryType.Savings
        ? -transaction.amount
        : transaction.amount;

    netByCategory.set(transaction.categoryId, {
      net: (netByCategory.get(transaction.categoryId)?.net ?? 0) + signedAmount,
      type: category.type,
    });
  });

  function categoryLabel(categoryId: string): string {
    const category = categories.get(categoryId);
    if (!category) return "Uncategorized";
    return category.emoji
      ? `${category.emoji} ${category.name}`
      : category.name;
  }

  function sumMagnitudes(entries: CategoryNetEntry[]): number {
    return entries.reduce((sum, [, entry]) => sum + Math.abs(entry.net), 0);
  }

  // A category is placed by which way it actually moved money over the period,
  // not by its type. An expense category dominated by refunds is an inflow; an
  // income category dominated by a clawback is an outflow. A category that nets
  // to exactly zero has no flow to draw, so it's dropped.
  const byLargestFirst = (a: CategoryNetEntry, b: CategoryNetEntry) =>
    Math.abs(b[1].net) - Math.abs(a[1].net);
  const allEntries = Array.from(netByCategory.entries());
  const inflowEntries = allEntries
    .filter(([, entry]) => entry.net > 0)
    .sort(byLargestFirst);
  const outflowEntries = allEntries
    .filter(([, entry]) => entry.net < 0)
    .sort(byLargestFirst);

  if (
    inflowEntries.length === 0 &&
    outflowEntries.length === 0 &&
    unreviewedNet === 0
  ) {
    return {
      nodes: [],
      transactionsByNode: new Map(),
      links: [],
      totalIncome: 0,
      totalExpenses: 0,
      totalSavings: 0,
      totalFlow: 0,
      maxColumnNodeCount: 0,
      outerLabels: { left: [], right: [] },
      currency,
    };
  }

  const isIncome = ([, entry]: CategoryNetEntry) =>
    entry.type === CategoryType.Income;
  const incomeInflows = inflowEntries.filter(isIncome);
  // Expense and savings categories that ended up bringing money in.
  const reversedOutflows = inflowEntries.filter((entry) => !isIncome(entry));
  const expenseOutflows = outflowEntries.filter(
    ([, entry]) => entry.type === CategoryType.Expense,
  );
  const savingsOutflows = outflowEntries.filter(
    ([, entry]) => entry.type === CategoryType.Savings,
  );
  // Income categories that ended up taking money out.
  const reversedIncome = outflowEntries.filter(isIncome);

  const totalIncome = sumMagnitudes(incomeInflows);
  const totalExpenses = sumMagnitudes(expenseOutflows);
  const totalSavings = sumMagnitudes(savingsOutflows);

  const grossInflow =
    totalIncome + sumMagnitudes(reversedOutflows) + Math.max(0, unreviewedNet);
  const grossOutflow =
    totalExpenses +
    totalSavings +
    sumMagnitudes(reversedIncome) +
    Math.max(0, -unreviewedNet);

  // Inflows and outflows rarely balance exactly, and a Sankey has to conserve
  // flow, so the difference becomes its own node on whichever side is short.
  const deficit = Math.max(0, grossOutflow - grossInflow);
  const surplus = Math.max(0, grossInflow - grossOutflow);
  const totalFlow = grossInflow + deficit; // === grossOutflow + surplus

  const nodes: MoneyFlowNode[] = [];
  const links: MoneyFlowLink[] = [];

  function addCategoryNodes(
    entries: CategoryNetEntry[],
    nodeColor: string,
    link: (categoryId: string, magnitude: number) => MoneyFlowLink,
    columnOverride?: MoneyFlowColumnOverride,
  ) {
    entries.forEach(([categoryId, entry]) => {
      nodes.push({
        id: categoryId,
        nodeLabel: categoryLabel(categoryId),
        nodeColor,
        columnOverride,
      });
      links.push(link(categoryId, Math.abs(entry.net)));
    });
  }

  // --- Inflow side -----------------------------------------------------------

  addCategoryNodes(incomeInflows, INCOME_COLOR, (categoryId, magnitude) => ({
    source: categoryId,
    target: TOTAL_INCOME_NODE_ID,
    value: magnitude,
    drilldownNodeId: categoryId,
  }));

  // Reversed categories keep their type's color, so a blue node sitting on the
  // inflow side reads as the anomaly it is.
  reversedOutflows.forEach(([categoryId, entry]) => {
    nodes.push({
      id: categoryId,
      nodeLabel: categoryLabel(categoryId),
      nodeColor:
        entry.type === CategoryType.Savings ? SAVINGS_COLOR : EXPENSE_COLOR,
    });
    links.push({
      source: categoryId,
      target: TOTAL_FLOW_NODE_ID,
      value: Math.abs(entry.net),
      drilldownNodeId: categoryId,
    });
  });

  if (totalIncome > 0) {
    nodes.push({
      id: TOTAL_INCOME_NODE_ID,
      nodeLabel: "Total Income",
      nodeColor: TOTAL_INCOME_COLOR,
    });
    links.push({
      source: TOTAL_INCOME_NODE_ID,
      target: TOTAL_FLOW_NODE_ID,
      value: totalIncome,
      drilldownNodeId: TOTAL_INCOME_NODE_ID,
    });
  }

  if (deficit > 0) {
    // More went out than came in — whether that's drawdown of an existing
    // balance or income the user hasn't categorized, this report can't tell.
    // Nothing flows into it, so d3-sankey would otherwise strand it in the
    // leftmost column among the categories; it belongs beside Total Income.
    nodes.push({
      id: UNACCOUNTED_INFLOW_NODE_ID,
      nodeLabel: "Unaccounted Inflow",
      nodeColor: BALANCING_COLOR,
      columnOverride: totalIncome > 0 ? 1 : 0,
    });
    links.push({
      source: UNACCOUNTED_INFLOW_NODE_ID,
      target: TOTAL_FLOW_NODE_ID,
      value: deficit,
      drilldownNodeId: UNACCOUNTED_INFLOW_NODE_ID,
    });
  }

  if (unreviewedNet !== 0) {
    nodes.push({
      id: UNREVIEWED_NODE_ID,
      nodeLabel: "Unreviewed",
      nodeColor: UNREVIEWED_COLOR,
      columnOverride: unreviewedNet > 0 ? 0 : "lastColumn",
    });
    links.push({
      source: unreviewedNet > 0 ? UNREVIEWED_NODE_ID : TOTAL_FLOW_NODE_ID,
      target: unreviewedNet > 0 ? TOTAL_FLOW_NODE_ID : UNREVIEWED_NODE_ID,
      value: Math.abs(unreviewedNet),
      drilldownNodeId: UNREVIEWED_NODE_ID,
    });
  }

  // --- The waist -------------------------------------------------------------

  nodes.push({
    id: TOTAL_FLOW_NODE_ID,
    nodeLabel: "Total Inflow / Total Outflow",
    nodeColor: TOTAL_FLOW_COLOR,
  });

  // --- Outflow side ----------------------------------------------------------

  function addOutflowBranch(
    branchNodeId: string,
    branchLabel: string,
    branchColor: string,
    branchTotal: number,
    entries: CategoryNetEntry[],
    categoryColor: string,
  ) {
    if (branchTotal <= 0) return;

    nodes.push({
      id: branchNodeId,
      nodeLabel: branchLabel,
      nodeColor: branchColor,
    });
    links.push({
      source: TOTAL_FLOW_NODE_ID,
      target: branchNodeId,
      value: branchTotal,
      drilldownNodeId: branchNodeId,
    });

    addCategoryNodes(entries, categoryColor, (categoryId, magnitude) => ({
      source: branchNodeId,
      target: categoryId,
      value: magnitude,
      drilldownNodeId: categoryId,
    }));
  }

  addOutflowBranch(
    TOTAL_EXPENSES_NODE_ID,
    "Total Expenses",
    TOTAL_EXPENSES_COLOR,
    totalExpenses,
    expenseOutflows,
    EXPENSE_COLOR,
  );
  addOutflowBranch(
    TOTAL_SAVINGS_NODE_ID,
    "Total Savings",
    TOTAL_SAVINGS_COLOR,
    totalSavings,
    savingsOutflows,
    SAVINGS_COLOR,
  );

  if (surplus > 0) {
    // More came in than went out — the remainder isn't accounted for by any
    // category. It sits beside the outflow totals, since that's what it's an
    // alternative to.
    nodes.push({
      id: UNACCOUNTED_OUTFLOW_NODE_ID,
      nodeLabel: "Unaccounted Outflow",
      nodeColor: BALANCING_COLOR,
    });
    links.push({
      source: TOTAL_FLOW_NODE_ID,
      target: UNACCOUNTED_OUTFLOW_NODE_ID,
      value: surplus,
      drilldownNodeId: UNACCOUNTED_OUTFLOW_NODE_ID,
    });
  }

  // These hang straight off Total Outflow, so their inferred column would be
  // the one holding the outflow totals. Pin them to the last column instead, to
  // keep every individual category lined up together.
  addCategoryNodes(
    reversedIncome,
    INCOME_COLOR,
    (categoryId, magnitude) => ({
      source: TOTAL_FLOW_NODE_ID,
      target: categoryId,
      value: magnitude,
      drilldownNodeId: categoryId,
    }),
    "lastColumn",
  );

  const maxColumnNodeCount = Math.max(
    // Leftmost: every category that brought money in.
    incomeInflows.length +
      reversedOutflows.length +
      (unreviewedNet > 0 ? 1 : 0),
    // Total Income and Unaccounted Inflow share a column.
    (totalIncome > 0 ? 1 : 0) + (deficit > 0 ? 1 : 0),
    // The outflow totals and Unaccounted Outflow share a column.
    (totalExpenses > 0 ? 1 : 0) +
      (totalSavings > 0 ? 1 : 0) +
      (surplus > 0 ? 1 : 0),
    // Rightmost: every category that took money out.
    expenseOutflows.length +
      savingsOutflows.length +
      reversedIncome.length +
      (unreviewedNet < 0 ? 1 : 0),
  );

  const labelsFor = (entries: CategoryNetEntry[]) =>
    entries.map(([categoryId]) => categoryLabel(categoryId));

  // Keep the backing transactions alongside the graph so drilldowns use the
  // exact same date, review, currency, and net-category rules as the chart.
  const transactionsByNode = new Map<string, BreadTransaction[]>();
  for (const [categoryId] of [...inflowEntries, ...outflowEntries]) {
    transactionsByNode.set(
      categoryId,
      filteredTransactions.filter(
        (transaction) =>
          transaction.reviewed && transaction.categoryId === categoryId,
      ),
    );
  }
  const transactionsFor = (entries: CategoryNetEntry[]) =>
    entries.flatMap(([categoryId]) => transactionsByNode.get(categoryId) ?? []);
  transactionsByNode.set(TOTAL_INCOME_NODE_ID, transactionsFor(incomeInflows));
  transactionsByNode.set(
    TOTAL_EXPENSES_NODE_ID,
    transactionsFor(expenseOutflows),
  );
  transactionsByNode.set(
    TOTAL_SAVINGS_NODE_ID,
    transactionsFor(savingsOutflows),
  );
  const unreviewedTransactions =
    unreviewedNet !== 0
      ? filteredTransactions.filter((transaction) => !transaction.reviewed)
      : [];
  transactionsByNode.set(UNREVIEWED_NODE_ID, unreviewedTransactions);

  return {
    nodes,
    transactionsByNode,
    links,
    totalIncome,
    totalExpenses,
    totalSavings,
    totalFlow,
    maxColumnNodeCount,
    outerLabels: {
      left: [
        ...labelsFor([...incomeInflows, ...reversedOutflows]),
        ...(unreviewedNet > 0 ? ["Unreviewed"] : []),
      ],
      right: [
        ...labelsFor([
          ...expenseOutflows,
          ...savingsOutflows,
          ...reversedIncome,
        ]),
        ...(unreviewedNet < 0 ? ["Unreviewed"] : []),
      ],
    },
    currency,
  };
}

/** The group node selected by either a node click or a band click. */
export type MoneyFlowSelection = string;

export function getMoneyFlowDrilldown(
  data: MoneyFlowData,
  selection: MoneyFlowSelection | null,
): {
  groupLabel: string;
  transactions: BreadTransaction[];
  explanation?: string;
} | null {
  if (!selection || selection === TOTAL_FLOW_NODE_ID) return null;
  const node = data.nodes.find((node) => node.id === selection);
  if (!node) return null;

  const groupLabel = node.nodeLabel;
  if (
    selection === UNACCOUNTED_INFLOW_NODE_ID ||
    selection === UNACCOUNTED_OUTFLOW_NODE_ID
  ) {
    return {
      groupLabel,
      transactions: [],
      explanation:
        "This is the difference between the inflows and outflows shown in the chart. It is a calculated balancing amount, so no individual transactions belong to it.",
    };
  }

  const transactions = data.transactionsByNode.get(selection) ?? [];
  return {
    groupLabel,
    transactions,
  };
}
